import { getSessionAuthorizationHeaders } from '../services/sessionAuth';
import { useState, useEffect, useRef } from 'react';
import { recordAudio, stopRecording, pauseRecording, resumeRecording, resetAudioChunks } from '../components/recordAudio';
import { useSpeechToText } from '../components/speechToText';
import {
  claimTTSPlayer,
  isPersistentTTSPlayer,
  playChunkedTTS,
  primeTTSPlayer,
  releaseTTSPlayer,
  textToSpeech,
} from '../components/textToSpeech';
import { Message } from '../models/Message';
import { isIOS, isSafari } from '../utils';
import {
  ConversationMode,
  RestoredConversation,
  loadConversation,
  saveConversation,
} from '../services/conversationPersistence';

const RATE_LIMIT_MSG = 'Demasiados mensajes en poco tiempo, favor de esperar un momento.';
const MAX_API_CONTEXT_MESSAGES = 40;

const wait = (milliseconds: number) =>
  new Promise<void>(resolve => {
    window.setTimeout(resolve, milliseconds);
  });

const limitApiContext = (
  firstMessage: Message[],
  context: Message[]
): Message[] => {
  const systemMessage = firstMessage[0];

  if (!systemMessage) {
    return context.slice(-MAX_API_CONTEXT_MESSAGES);
  }

  const startsWithSystem =
    context[0]?.role === systemMessage.role &&
    context[0]?.content === systemMessage.content;

  const conversationalMessages = startsWithSystem
    ? context.slice(1)
    : context;

  return [
    systemMessage,
    ...conversationalMessages.slice(
      -MAX_API_CONTEXT_MESSAGES
    ),
  ];
};


interface UseChatSessionOptions {
  initialConversation?: RestoredConversation;
  onPersistenceConflict?: () => void;
  language: string;
  mode: ConversationMode;
  firstMessage: Message[];
  messagesLimit: number;
  feedbackThreshold: number;
  alwaysTTS?: boolean;
  neverAutoTTS?: boolean;
  chunkedManualPlay?: boolean;
}

export function useChatSession({
  initialConversation,
  onPersistenceConflict,
  language,
  mode,
  firstMessage,
  messagesLimit,
  feedbackThreshold,
  alwaysTTS = false,
  neverAutoTTS = false,
  chunkedManualPlay = false,
}: UseChatSessionOptions) {
  const { transcribe } = useSpeechToText();
  const conversationIdRef = useRef<number | undefined>(initialConversation?.id);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const saveErrorRef = useRef<Error | null>(null);
  const [persistenceError, setPersistenceError] = useState('');


  const [messages, setMessages] = useState<Message[]>(firstMessage);
  const [input, setInput] = useState('');
  const [showSendIcon, setShowSendIcon] = useState(false);
  const [transcription, setTranscription] = useState<string | null>(null);
  const [isMicCanceled, setIsMicCanceled] = useState(false);
  const [isPauseStarted, setIsPauseStarted] = useState(false);
  const [isAudioPaused, setIsAudioPaused] = useState(false);
  const [errorAppeared, setErrorAppeared] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [isMicActivated, setIsMicActivated] = useState(false);
  const [isStopRecordingAvailable, setIsStopRecordingAvailable] = useState(false);
  const [audioConfirmed, setAudioConfirmed] = useState(false);
  const [isConversationReady, setIsConversationReady] =
    useState(false);

  // Persistent Audio object — avoids re-creating on every render
  const audioRef = useRef<HTMLAudioElement>(new Audio());

  // API context — what actually gets sent to the API. Starts equal to firstMessage,
  // grows with each exchange, and can be reset on topic change (display messages are unaffected).
  const apiContextRef = useRef<Message[]>([...firstMessage]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Audio caches — keyed by message index, live for the browser session
  const botAudioCacheRef  = useRef<Map<number, string>>(new Map());
  const userAudioCacheRef = useRef<Map<number, string>>(new Map());
  const [userAudioIndices, setUserAudioIndices] = useState<Set<number>>(new Set());

  // Shared playback ref — ensures only one on-demand audio plays at a time
  const playbackRef = useRef<HTMLAudioElement | null>(null);
  const manualAudioRef = useRef<HTMLAudioElement | null>(null);

  // Cancel handle for in-progress chunked TTS auto-play
  const cancelChunkedTTSRef = useRef<(() => void) | null>(null);
  const [isBotSpeaking, setIsBotSpeaking] = useState(false);
  const [playingIndex, setPlayingIndex]   = useState<number | null>(null);
  const [pausedPlaybackIndex, setPausedPlaybackIndex] =
    useState<number | null>(null);
  const [isWaitingForBot, setIsWaitingForBot] = useState(false);
  const ttsResolveRef    = useRef<(() => void) | null>(null);
  const keepMicActiveRef = useRef(false);

  const stopCurrentPlayback = () => {
    const audio = playbackRef.current;

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.onended = null;
      audio.onerror = null;

      if (isPersistentTTSPlayer(audio)) {
        releaseTTSPlayer();
      }

      playbackRef.current = null;
    }

    cancelChunkedTTSRef.current?.();
    cancelChunkedTTSRef.current = null;
    setPlayingIndex(null);
    setPausedPlaybackIndex(null);
  };

  const pauseCurrentPlayback = () => {
    const audio = playbackRef.current;

    if (!audio || playingIndex === null) {
      return;
    }

    audio.pause();
    setPausedPlaybackIndex(playingIndex);
    setPlayingIndex(null);
  };

  const resumeCurrentPlayback = () => {
    const audio = playbackRef.current;

    if (!audio || pausedPlaybackIndex === null) {
      return;
    }

    const index = pausedPlaybackIndex;

    setPausedPlaybackIndex(null);
    setPlayingIndex(index);

    void audio.play().catch(() => {
      playbackRef.current = null;
      setPlayingIndex(null);
      setPausedPlaybackIndex(null);
    });
  };

  // --- Action functions ---

  const cancelMicFlow = () => {
    cancelChunkedTTSRef.current?.();
    cancelChunkedTTSRef.current = null;
    ttsResolveRef.current?.();
    ttsResolveRef.current = null;
    setIsBotSpeaking(false);
    setIsMicActivated(false);
    setIsStopRecordingAvailable(false);
    setIsMicCanceled(true);
    audioRef.current.pause();
  };

  const resumeAudioPaused = () => {
    setIsAudioPaused(false);
    resumeRecording();
  };

  const pauseAudio = async () => {
    setIsPauseStarted(true);
    setIsAudioPaused(true);
    await pauseRecording();
  };

  const cancelAudioFlow = async () => {
    if (isPauseStarted) {
      setIsPauseStarted(false);
      setIsAudioPaused(false);
      resetAudioChunks();
    }
    setIsMicActivated(false);
    setIsStopRecordingAvailable(false);
    setAudioBlob(await stopRecording());
  };

  const confirmSendAudio = async () => {
    primeTTSPlayer(); // prime the persistent audio element while inside the user gesture
    setAudioConfirmed(true);
    if (isPauseStarted) {
      await pauseRecording();
      setIsAudioPaused(false);
      setIsPauseStarted(false);
      setAudioBlob(await stopRecording());
      setIsStopRecordingAvailable(false);
      resetAudioChunks();
    } else {
      setAudioBlob(await stopRecording());
      setIsStopRecordingAvailable(false);
    }
  };

  const toggleTranscription = () => {
    setIsMicActivated(prev => !prev);
    setIsMicCanceled(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInput(event.target.value);
    setShowSendIcon(event.target.value.length > 0);
  };

  const activateMic = () => {
    setErrorAppeared(false);
    if (isBotSpeaking) {
      keepMicActiveRef.current = true;
      cancelChunkedTTSRef.current?.();
      cancelChunkedTTSRef.current = null;
      ttsResolveRef.current?.();
      ttsResolveRef.current = null;
      setIsBotSpeaking(false);
      transcribeAudio();
      return;
    }
    if (
      playingIndex !== null ||
      pausedPlaybackIndex !== null
    ) {
      stopCurrentPlayback();
    }
    toggleTranscription();
  };

  const stopBotSpeaking = () => {
    cancelChunkedTTSRef.current?.();
    cancelChunkedTTSRef.current = null;
    ttsResolveRef.current?.();
    ttsResolveRef.current = null;
  };

  const sendInput = () => {
    primeTTSPlayer(); // prime within user gesture before async API call
    setErrorAppeared(false);
    handleSubmit(input);
    setAudioURL(null);
  };

  const editLastMessage = async (newContent: string) => {
    let lastUserIdx = -1;
    for (let i = messages.length - 1; i >= 1; i--) {
      if (messages[i].role === 'user') { lastUserIdx = i; break; }
    }
    if (lastUserIdx === -1) return;
    setInput('');
    setShowSendIcon(false);
    setErrorAppeared(false);
    // Trim display messages
    const trimmedDisplay: Message[] = [...messages.slice(0, lastUserIdx), { role: 'user', content: newContent }];
    setMessages(trimmedDisplay);
    botAudioCacheRef.current.delete(trimmedDisplay.length);
    // Trim API context to match: find last user message in context and replace it
    const ctx = apiContextRef.current;
    let lastUserApiIdx = -1;
    for (let i = ctx.length - 1; i >= 1; i--) {
      if (ctx[i].role === 'user') { lastUserApiIdx = i; break; }
    }
    const trimmedApi: Message[] = lastUserApiIdx >= 0
      ? [...ctx.slice(0, lastUserApiIdx), { role: 'user', content: newContent }]
      : [...trimmedDisplay];
    apiContextRef.current = trimmedApi;
    setIsWaitingForBot(true);
    try {
      const res = await fetch('/chat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionAuthorizationHeaders() },
        body: JSON.stringify({ messages: trimmedApi }),
      });
      if (res.status === 429) {
        setIsWaitingForBot(false);
        setMessages(prev => [...prev, { role: 'assistant', content: RATE_LIMIT_MSG }]);
        return;
      }
      const responseData = await res.json();
      const assistantResponse: string = responseData.choices[0].message.content;
      setIsWaitingForBot(false);
      setMessages(prev => [...prev, { role: 'assistant', content: assistantResponse }]);
      apiContextRef.current = [...trimmedApi, { role: 'assistant', content: assistantResponse }];
      if (alwaysTTS) {
        setIsBotSpeaking(true);
        await new Promise<void>((resolve) => {
          ttsResolveRef.current = resolve;
          cancelChunkedTTSRef.current = playChunkedTTS(assistantResponse, () => {
            ttsResolveRef.current = null;
            resolve();
          });
        });
        cancelChunkedTTSRef.current = null;
        ttsResolveRef.current = null;
        setIsBotSpeaking(false);
      }
    } catch (error) {
      console.error('Error editing message:', error);
      setIsWaitingForBot(false);
      setErrorAppeared(true);
    }
  };

  const playLastAudio = () => {
    if (!audioURL) return;
    new Audio(audioURL).play();
  };

  const playBotMessageAudio = async (index: number, content: string) => {
    stopCurrentPlayback();

    const cachedUrl = botAudioCacheRef.current.get(index);

    if (cachedUrl) {
      const audio = claimTTSPlayer();
      playbackRef.current = audio;
      setPlayingIndex(index);
      setPausedPlaybackIndex(null);

      const onEnd = () => {
        if (playbackRef.current === audio) {
          playbackRef.current = null;
        }
        audio.onended = null;
        audio.onerror = null;
        releaseTTSPlayer();
        setPlayingIndex(null);
        setPausedPlaybackIndex(null);
      };

      audio.pause();
      audio.currentTime = 0;
      audio.src = cachedUrl;
      audio.onended = onEnd;
      audio.onerror = onEnd;
      void audio.play().catch(onEnd);
      return;
    }

    primeTTSPlayer();
    const audio = claimTTSPlayer();
    playbackRef.current = audio;
    setPlayingIndex(index);
    setPausedPlaybackIndex(null);

    const onEnd = () => {
      if (playbackRef.current === audio) {
        playbackRef.current = null;
      }
      audio.onended = null;
      audio.onerror = null;
      releaseTTSPlayer();
      setPlayingIndex(null);
      setPausedPlaybackIndex(null);
    };

    let blob = await textToSpeech(content);

    if (!blob && playbackRef.current === audio) {
      await wait(250);
      blob = await textToSpeech(content);
    }

    if (!blob || playbackRef.current !== audio) {
      onEnd();
      return;
    }

    const url = URL.createObjectURL(blob);
    botAudioCacheRef.current.set(index, url);

    audio.pause();
    audio.currentTime = 0;
    audio.src = url;
    audio.onended = onEnd;
    audio.onerror = onEnd;

    void audio.play().catch(onEnd);
  };

  const playUserMessageAudio = (index: number) => {
    stopCurrentPlayback();
    const url = userAudioCacheRef.current.get(index);
    if (url) {
      const audio = manualAudioRef.current ?? new Audio();
      manualAudioRef.current = audio;
      playbackRef.current = audio;
      setPlayingIndex(index);
      setPausedPlaybackIndex(null);

      const onEnd = () => {
        if (playbackRef.current === audio) {
          playbackRef.current = null;
        }
        audio.onended = null;
        audio.onerror = null;
        setPlayingIndex(null);
        setPausedPlaybackIndex(null);
      };

      audio.pause();
      audio.currentTime = 0;
      audio.src = url;
      audio.onended = onEnd;
      audio.onerror = onEnd;
      void audio.play().catch(onEnd);
    }
  };

  const evictBotAudioCache = (index: number) => {
    if (
      playingIndex === index ||
      pausedPlaybackIndex === index
    ) {
      stopCurrentPlayback();
    }

    const url = botAudioCacheRef.current.get(index);
    if (url) URL.revokeObjectURL(url);
    botAudioCacheRef.current.delete(index);
  };

  const getUserAudioUrl = (index: number): string | undefined =>
    userAudioCacheRef.current.get(index);

  const fetchSuggestion = async (prompt?: string): Promise<string | null> => {
    try {
      const suggestionMessages: Message[] = [
        ...apiContextRef.current,
        { role: 'user', content: prompt ?? 'Suggest ONE short natural reply I could say next as a language learner. Reply with ONLY the suggested text, no explanation.' },
      ];
      const res = await fetch('/chat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionAuthorizationHeaders() },
        body: JSON.stringify({ messages: suggestionMessages }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.choices[0].message.content;
    } catch {
      return null;
    }
  };

  // --- Async audio / API functions ---

  const playAudioWithPause = (audioBlobToPlay: Blob, assistantResponse: string): Promise<void> =>
    new Promise((resolve) => {
      try {
        const audioUrl = URL.createObjectURL(audioBlobToPlay);
        audioRef.current.src = audioUrl;
        setAudioURL(audioUrl);
        audioRef.current.onended = () => resolve();
        audioRef.current.play();
        if (isIOS() || isSafari()) {
          const timeSpeaking = (assistantResponse.split(' ').length / 3.33) * 1000;
          setTimeout(resolve, timeSpeaking);
        }
      } catch (error) {
        console.error('Error playing audio:', error);
        resolve();
      }
    });

  const transcribeAudio = async () => {
    if (Math.floor(messages.length / 2) >= messagesLimit) {
      setIsMicActivated(false);
      setIsStopRecordingAvailable(false);
      stopRecording();
    } else {
      setIsStopRecordingAvailable(true);
      recordAudio();
    }
  };

  const handleSubmit = async (newInput: string) => {
    setInput('');
    const newUserMsg: Message = { role: 'user', content: newInput };
    setMessages(prev => [...prev, newUserMsg]);
    const apiMessages: Message[] = [...apiContextRef.current, newUserMsg];
    apiContextRef.current = apiMessages;
    setIsWaitingForBot(true);
    try {
      const res = await fetch('/chat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionAuthorizationHeaders() },
        body: JSON.stringify({ messages: apiMessages }),
      });
      if (res.status === 429) {
        apiContextRef.current = apiMessages.slice(0, -1); // roll back user message
        setIsWaitingForBot(false);
        setMessages(prev => [...prev, { role: 'assistant', content: RATE_LIMIT_MSG }]);
        setIsMicActivated(false);
        setIsStopRecordingAvailable(false);
        return;
      }
      const responseData = await res.json();
      const assistantResponse: string = responseData.choices[0].message.content;
      setIsWaitingForBot(false);
      setMessages(prev => [...prev, { role: 'assistant', content: assistantResponse }]);
      apiContextRef.current = [...apiMessages, { role: 'assistant', content: assistantResponse }];
      setShowSendIcon(false);
      if (isMicActivated || alwaysTTS) {
        if (!neverAutoTTS) {
          setIsBotSpeaking(true);
          await new Promise<void>((resolve) => {
            ttsResolveRef.current = resolve;
            cancelChunkedTTSRef.current = playChunkedTTS(assistantResponse, () => {
              ttsResolveRef.current = null;
              resolve();
            });
          });
          cancelChunkedTTSRef.current = null;
          ttsResolveRef.current = null;
          setIsBotSpeaking(false);
        }
        if (isMicActivated) {
          if (!keepMicActiveRef.current) {
            // Return to idle — user presses mic again to continue
            setIsMicActivated(false);
            setIsStopRecordingAvailable(false);
          }
          keepMicActiveRef.current = false;
        }
      }
    } catch (error) {
      console.error('Error fetching data from OpenAI API', error);
      setIsWaitingForBot(false);
    }
  };

  const changeTopic = async (topic: string, customContent?: string) => {
    primeTTSPlayer(); // prime within user gesture before async API call
    const topicMsg: Message = {
      role: 'user',
      content: customContent ?? `Let's switch topics. Please start a fresh conversation about: ${topic}. Keep it natural, as if we just started talking, without greeting again or confirming the switch.`,
    };
    const previousContext = apiContextRef.current;
    const resetContext: Message[] = [firstMessage[0], topicMsg];
    apiContextRef.current = resetContext;
    setIsWaitingForBot(true);
    try {
      const res = await fetch('/chat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionAuthorizationHeaders() },
        body: JSON.stringify({ messages: resetContext }),
      });
      if (res.status === 429) {
        apiContextRef.current = previousContext; // restore previous context
        setIsWaitingForBot(false);
        setMessages(prev => [...prev, { role: 'assistant', content: RATE_LIMIT_MSG }]);
        return;
      }
      const data = await res.json();
      const assistantResponse: string = data.choices[0].message.content;
      setIsWaitingForBot(false);
      setMessages(prev => [...prev, { role: 'assistant', content: assistantResponse }]);
      apiContextRef.current = [...resetContext, { role: 'assistant', content: assistantResponse }];
      if (alwaysTTS) {
        setIsBotSpeaking(true);
        await new Promise<void>((resolve) => {
          ttsResolveRef.current = resolve;
          cancelChunkedTTSRef.current = playChunkedTTS(assistantResponse, () => {
            ttsResolveRef.current = null;
            resolve();
          });
        });
        cancelChunkedTTSRef.current = null;
        ttsResolveRef.current = null;
        setIsBotSpeaking(false);
      }
    } catch (error) {
      console.error('Error changing topic:', error);
      setIsWaitingForBot(false);
    }
  };

  // --- Effects ---

  // Process recorded audio blob when available
  useEffect(() => {
    if (!audioBlob || !isMicActivated) return;
    const processAudio = async () => {
      try {
        const text = await transcribe(audioBlob, language);
        if (text) setTranscription(text);
      } catch (error) {
        console.error('Error transcribing audio', error);
        setErrorAppeared(true);
        await cancelAudioFlow();
      }
    };
    processAudio();
  }, [audioBlob]);

  // Keyboard shortcuts for mic controls
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && isStopRecordingAvailable && !isMicCanceled) {
        confirmSendAudio();
      }
      if (event.key === ' ') {
        if (isStopRecordingAvailable && !isMicCanceled && !isAudioPaused) pauseAudio();
        else if (isStopRecordingAvailable && !isMicCanceled && isPauseStarted && isAudioPaused) resumeAudioPaused();
      }
      if (event.key === 'Escape') {
        if (isStopRecordingAvailable && !isMicCanceled) cancelAudioFlow();
        else if (!isStopRecordingAvailable && isMicActivated) cancelMicFlow();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isStopRecordingAvailable, isMicCanceled, isAudioPaused, isMicActivated, isPauseStarted]);

  // Auto-submit when transcription is ready — store mic audio first
  useEffect(() => {
    if (transcription && isMicActivated) {
      if (audioBlob) {
        const userMsgIndex = messages.length;
        const url = URL.createObjectURL(audioBlob);
        userAudioCacheRef.current.set(userMsgIndex, url);
        setUserAudioIndices(prev => new Set(prev).add(userMsgIndex));
      }
      handleSubmit(transcription);
    }
  }, [transcription]);

  // Restore an existing conversation before requesting a new greeting.
  useEffect(() => {
    let cancelled = false;

    const initialiseConversation = async () => {
      setIsConversationReady(false);
      setIsWaitingForBot(true);

      try {
        const restoredConversation:
          RestoredConversation | null =
            initialConversation ?? await loadConversation(language, mode);

        if (cancelled) return;

        conversationIdRef.current = restoredConversation?.id;
        if (restoredConversation && restoredConversation.messages.length > 0) {
          const restoredDisplay = [
            ...firstMessage,
            ...restoredConversation.messages,
          ];

          const restoredApi =
            restoredConversation.apiContext.length > 0
              ? restoredConversation.apiContext
              : restoredDisplay;

          setMessages(restoredDisplay);

          apiContextRef.current = limitApiContext(
            firstMessage,
            restoredApi
          );

          return;
        }

        const res = await fetch('/chat.php', {
          method: 'POST',
          headers: {
            ...getSessionAuthorizationHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messages: firstMessage,
          }),
        });

        if (res.status === 429) {
          setMessages([
            ...firstMessage,
            {
              role: 'assistant',
              content: RATE_LIMIT_MSG,
            },
          ]);

          return;
        }

        if (!res.ok) {
          throw new Error(
            `HTTP error! status: ${res.status}`
          );
        }

        const data = await res.json();
        if (cancelled) return;

        const assistantResponse: string =
          data.choices[0].message.content;

        const assistantMessage: Message = {
          role: 'assistant',
          content: assistantResponse,
        };

        setMessages([
          ...firstMessage,
          assistantMessage,
        ]);

        apiContextRef.current = [
          ...firstMessage,
          assistantMessage,
        ];

        if (alwaysTTS) {
          setIsBotSpeaking(true);

          await new Promise<void>((resolve) => {
            ttsResolveRef.current = resolve;

            cancelChunkedTTSRef.current =
              playChunkedTTS(
                assistantResponse,
                () => {
                  ttsResolveRef.current = null;
                  resolve();
                }
              );
          });

          cancelChunkedTTSRef.current = null;
          ttsResolveRef.current = null;
          setIsBotSpeaking(false);
        }
      } catch (error) {
        console.error(
          'Error initializing the RETZIA conversation:',
          error
        );

        setErrorAppeared(true);
      } finally {
        if (!cancelled) {
          setIsWaitingForBot(false);
          setIsConversationReady(true);
        }
      }
    };

    initialiseConversation();

    return () => {
      cancelled = true;
    };
  }, [language, mode, firstMessage, alwaysTTS, initialConversation]);

  // Save completed exchanges without making additional OpenAI calls.
  useEffect(() => {
    if (!isConversationReady) return;

    const lastMessage =
      messages[messages.length - 1];

    if (
      !lastMessage ||
      lastMessage.role !== 'assistant' ||
      lastMessage.content === RATE_LIMIT_MSG
    ) {
      return;
    }

    const visibleMessages = messages.slice(1);

    const persistedMessages =
      visibleMessages.filter(
        (message, index) =>
          message.content !== RATE_LIMIT_MSG &&
          visibleMessages[index + 1]?.content !==
            RATE_LIMIT_MSG
      );

    const limitedApiContext = limitApiContext(
      firstMessage,
      apiContextRef.current
    );

    apiContextRef.current = limitedApiContext;

    const expectedId = conversationIdRef.current;
    saveQueueRef.current = saveQueueRef.current.catch(() => {}).then(async () => {
      try {
        await saveConversation(language, mode, persistedMessages, limitedApiContext, expectedId);
        saveErrorRef.current = null;
        setPersistenceError('');
      } catch (error) {
        saveErrorRef.current = error instanceof Error ? error : new Error('Error al guardar');
        setPersistenceError('No se pudo guardar este intercambio. Conserva el texto y recarga antes de iniciar otra conversación.');
        if (saveErrorRef.current.message === 'conversation_changed') onPersistenceConflict?.();
      }
    });
  }, [
    messages,
    isConversationReady,
    language,
    mode,
    firstMessage,
    onPersistenceConflict,
  ]);

  // Revoke all cached blob URLs when the component unmounts to free memory
  useEffect(() => {
    const botAudioCache = botAudioCacheRef.current;
    const userAudioCache = userAudioCacheRef.current;

    return () => {
      botAudioCache.forEach(url => URL.revokeObjectURL(url));
      userAudioCache.forEach(url => URL.revokeObjectURL(url));

      stopCurrentPlayback();

      if (manualAudioRef.current) {
        manualAudioRef.current.pause();
        manualAudioRef.current.src = '';
        manualAudioRef.current = null;
      }
    };
    // Audio caches live for the component session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start recording when mic is first activated; reset confirmed flag when deactivated
  useEffect(() => {
    if (isMicActivated) transcribeAudio();
    else setAudioConfirmed(false);
  }, [isMicActivated]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // --- Computed values ---

  const messageCount  = Math.floor(messages.length / 2) - 1;
  const isLimitReached  = messageCount >= messagesLimit;
  const isFeedbackShown = messageCount >= feedbackThreshold;
  const isRecording = isStopRecordingAvailable && !isMicCanceled;
  const isWaiting   = !isStopRecordingAvailable && isMicActivated;

  const prepareForNewConversation = async () => {
    await saveQueueRef.current;
    if (saveErrorRef.current) throw saveErrorRef.current;
    stopBotSpeaking();
    stopCurrentPlayback();
  };

  return {
    prepareForNewConversation,
    persistenceError,
    messages,
    input,
    showSendIcon,
    isMicCanceled,
    isPauseStarted,
    isAudioPaused,
    errorAppeared,
    audioURL,
    isMicActivated,
    isStopRecordingAvailable,
    messagesEndRef,
    messageCount,
    isLimitReached,
    isFeedbackShown,
    handleSubmit,
    handleChange,
    activateMic,
    sendInput,
    cancelMicFlow,
    confirmSendAudio,
    resumeAudioPaused,
    pauseAudio,
    cancelAudioFlow,
    playLastAudio,
    editLastMessage,
    playBotMessageAudio,
    playUserMessageAudio,
    userAudioIndices,
    fetchSuggestion,
    evictBotAudioCache,
    getUserAudioUrl,
    stopCurrentPlayback,
    pauseCurrentPlayback,
    resumeCurrentPlayback,
    pausedPlaybackIndex,
    audioConfirmed,
    changeTopic,
    isRecording,
    isWaiting,
    isBotSpeaking,
    stopBotSpeaking,
    playingIndex,
    isWaitingForBot,
    isConversationReady,
  };
}
